import Container from '../../components/shared/Container';
import PageHero from '../../components/shared/PageHero';
import wcagIcon from '../../images/wcag2.1AA.png';
import styles from './DeclaracionAccesibilidadPage.module.css';

export default function DeclaracionAccesibilidadPage() {
  return (
    <>
      <PageHero
        breadcrumb={[{ label: 'Inicio', to: '/' }, { label: 'Accesibilidad y usabilidad' }]}
        title="Accesibilidad y usabilidad"
        subtitle="Declaración de accesibilidad del portal www.solimat.com conforme al Real Decreto 1112/2018."
        image={null}
      />

      <Container as="section" className={styles.section}>
        <div className={styles.inner}>
          <article className={styles.content}>
            <div className={styles.badgeRow}>
              <img
                className={styles.badge}
                src={wcagIcon}
                width="88"
                height="32"
                alt="Icono de conformidad con el Nivel Doble-A de las Directrices de Accesibilidad para el Contenido Web 2.1 del W3C-WAI"
              />
              <span className={styles.badgeText}>WCAG 2.1 — Nivel AA (parcialmente conforme)</span>
            </div>

            <section aria-labelledby="estado-heading">
              <h2 id="estado-heading" className={styles.heading}>
                Estado de conformidad
              </h2>
              <p>
                Este sitio web cumple <strong>parcialmente</strong> con los requisitos de la norma{' '}
                <abbr title="Norma EN 301 549 V3.2.1">EN 301 549 V3.2.1</abbr> y las{' '}
                <abbr title="Web Content Accessibility Guidelines">WCAG</abbr> 2.1, nivel AA, debido a las
                excepciones e incumplimientos que se indican a continuación.
              </p>
              <p>
                Esta declaración se aplica al portal corporativo de SOLIMAT, accesible en{' '}
                <strong>www.solimat.com</strong>, gestionado por SOLIMAT MATEP Seg. Social nº 72, y fue
                preparada conforme al procedimiento establecido en el Real Decreto 1112/2018, de 7 de
                septiembre, sobre accesibilidad de los sitios web y aplicaciones para dispositivos móviles
                del sector público.
              </p>
            </section>

            <section aria-labelledby="contenido-heading">
              <h2 id="contenido-heading" className={styles.heading}>
                Contenido no accesible
              </h2>
              <p>El contenido que se recoge a continuación no es accesible por los siguientes motivos:</p>

              <h3 className={styles.subheading}>Incumplimientos de la norma</h3>
              <ul className={styles.list}>
                <li>
                  Algunos documentos en formato PDF publicados en las secciones de{' '}
                  <em>Recursos y herramientas</em> y <em>Perfil del contratante</em> no disponen de
                  etiquetado semántico completo ni de texto alternativo en sus tablas de datos (criterio
                  WCAG 1.3.1 — Información y relaciones).
                </li>
                <li>
                  El visor de la <em>Red de centros</em>, integrado mediante la API de Google Maps, presenta
                  controles con etiquetado insuficiente y ratios de contraste inferiores a 4,5:1 en algunos
                  estados (criterios WCAG 1.4.3 y 4.1.2). Se trata de un servicio de un tercero sobre el que
                  SOLIMAT tiene capacidad de modificación limitada.
                </li>
                <li>
                  Determinados contenidos de vídeo enlazados desde el portal pueden carecer de subtítulos o
                  de transcripción alternativa (criterios WCAG 1.2.2 y 1.2.3). Su subtitulado está
                  planificado en el siguiente ciclo de revisión.
                </li>
              </ul>

              <h3 className={styles.subheading}>Carga desproporcionada</h3>
              <p>
                No aplica. No se ha invocado la excepción de carga desproporcionada en ninguna parte del
                portal en este momento.
              </p>

              <h3 className={styles.subheading}>Contenido excluido del ámbito de aplicación</h3>
              <p>
                Los archivos de ofimática (PDF y similares) publicados antes del 23 de septiembre de 2018
                están excluidos conforme al artículo 3.2 del Real Decreto 1112/2018.
              </p>
            </section>

            <section aria-labelledby="usabilidad-heading">
              <h2 id="usabilidad-heading" className={styles.heading}>
                Medidas de accesibilidad y usabilidad aplicadas
              </h2>
              <ul className={styles.list}>
                <li>
                  Diseño adaptable (<em>responsive</em>) a móvil, tableta y escritorio.
                </li>
                <li>
                  Navegación completa mediante teclado y foco visible en todos los elementos interactivos.
                </li>
                <li>Estructura semántica con encabezados jerarquizados, listas y regiones ARIA.</li>
                <li>Texto alternativo en las imágenes con contenido informativo.</li>
                <li>Contraste de color revisado para cumplir el ratio mínimo de 4,5:1 en el texto.</li>
                <li>
                  Textos redactados en lenguaje claro y rutas de navegación (<em>migas de pan</em>) en todas
                  las páginas interiores.
                </li>
              </ul>
            </section>

            <section aria-labelledby="preparacion-heading">
              <h2 id="preparacion-heading" className={styles.heading}>
                Preparación de esta declaración
              </h2>
              <dl className={styles.definitionList}>
                <div className={styles.definitionItem}>
                  <dt>Fecha de la última revisión</dt>
                  <dd>
                    <time dateTime="2026-09">Septiembre de 2026</time>
                  </dd>
                </div>
                <div className={styles.definitionItem}>
                  <dt>Método empleado</dt>
                  <dd>
                    Autoevaluación realizada por el equipo de desarrollo de SOLIMAT, apoyada en herramientas
                    automáticas (axe DevTools, Lighthouse) y revisión manual conforme a las WCAG 2.1 nivel
                    AA.
                  </dd>
                </div>
                <div className={styles.definitionItem}>
                  <dt>Tecnologías de apoyo utilizadas</dt>
                  <dd>
                    NVDA, VoiceOver (iOS), navegación exclusiva por teclado y Chrome Accessibility DevTools.
                  </dd>
                </div>
                <div className={styles.definitionItem}>
                  <dt>Tecnologías en las que se basa la accesibilidad</dt>
                  <dd>HTML5, CSS3, WAI-ARIA 1.2 y JavaScript (React).</dd>
                </div>
              </dl>
            </section>

            <section aria-labelledby="contacto-heading">
              <h2 id="contacto-heading" className={styles.heading}>
                Comunicación y solicitud de información accesible
              </h2>
              <p>
                Puede comunicar cualquier incidencia de accesibilidad, así como solicitar información y
                contenidos que estén excluidos del ámbito de aplicación de la normativa, a través de los
                siguientes canales:
              </p>
              <ul className={styles.list}>
                <li>
                  Correo electrónico:{' '}
                  <a className={styles.link} href="mailto:web@solimat.com">
                    web@solimat.com
                  </a>
                </li>
                <li>
                  Teléfono:{' '}
                  <a className={styles.link} href="tel:925283186">
                    925 28 31 86
                  </a>
                </li>
                <li>
                  Formulario de la{' '}
                  <a className={styles.link} href="/oficina-virtual-de-reclamaciones">
                    Oficina virtual de reclamaciones
                  </a>
                </li>
              </ul>
              <p>
                Las comunicaciones serán recibidas y tratadas por la Dirección de SOLIMAT, que responderá en
                el plazo máximo de veinte días hábiles.
              </p>
            </section>

            <section aria-labelledby="reclamacion-heading">
              <h2 id="reclamacion-heading" className={styles.heading}>
                Procedimiento de reclamación
              </h2>
              <p>
                Si no recibe respuesta en el plazo indicado, o si considera que la respuesta recibida no
                satisface su solicitud, puede presentar una reclamación conforme al procedimiento previsto en
                el <strong>artículo 13 del Real Decreto 1112/2018</strong>, con el fin de conocer y oponerse
                a los motivos de la desestimación, instar la adopción de las medidas oportunas o exponer las
                razones por las que considera que la respuesta no cumple los requisitos exigidos.
              </p>
              <ul className={styles.list}>
                <li>
                  <a
                    className={styles.link}
                    href="https://www.inclusion.gob.es/accesibilidad"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Procedimiento de reclamación — Ministerio de Inclusión, Seguridad Social y Migraciones
                    (abre en una ventana nueva)
                  </a>
                </li>
                <li>
                  <a
                    className={styles.link}
                    href="https://rec.redsara.es/registro/action/are/acceso.do"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Registro Electrónico General de la Administración General del Estado (abre en una ventana
                    nueva)
                  </a>
                </li>
              </ul>
              <p className={styles.note}>
                Esta declaración fue elaborada el <time dateTime="2026-09-01">1 de septiembre de 2026</time>{' '}
                y será revisada anualmente o cuando se produzcan cambios significativos en el portal.
              </p>
            </section>
          </article>
        </div>
      </Container>
    </>
  );
}
